/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useRef} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {
  noZoomToolSpacing,
  ZoomInOutTool
} from '@ion/dls/src/maps/toolbar/ZoomInOutTool';
import {dlsZIndex} from '@ion/dls/src/theme/theme';
import {useMapBottomRightOffset} from '../hooks/useMapBottomRightOffset';
import {MapTools} from '../types/types';
import {BasemapGalleryTool} from './BasemapGalleryTool';
import {FullScreenToolFactory} from './FullScreenTool';
import HomeToolFactory from './HomeTool';

// defines the prop definition for GeofenceWidgetCluster component...
export interface GeofenceWidgetClusterProps {
  map: __esri.Map;
  view: __esri.MapView;
  createOSMBasemap: Function;
  onBasemapChanged: Function;
  activeTool: MapTools;
  setActiveTool: Function;
  isDisableInitialExtent?: boolean;
  callBack?: Function;
  showHome?: boolean;
  showBasemapGallery?: boolean;
  hideFullScreenOnPhone?: boolean;
}

// the panel's own content (header + basemap thumbnail grid) isn't measured
// directly - its height is known well enough from the current-theme layout
// (56px header + two rows of 64px thumbnails, titles, and padding) to
// estimate a gap that opens it just above the button rather than off past
// the top of the viewport ...
const ESTIMATED_PANEL_HEIGHT = 372;
const BUTTON_TO_PANEL_GAP    = 8;
const MIN_TOP_OFFSET         = 16;

const useStyles = makeStyles(() => ({
  cluster : {
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'flex-end',
    gap           : '16px',
    position      : 'relative',
    zIndex        : dlsZIndex('geofenceWidgetClusterRoot') as any
  },
  bottomRow : {
    display       : 'flex',
    flexDirection : 'row',
    alignItems    : 'center',
    gap           : '6px',
    marginRight   : '-6px'
  }
}));

/**
 * Defines the combined Admin Geofences map widget cluster for the current
 * theme, grouping the locate me, full screen, zoom stepper, and map styles
 * tools into a single positioned layout matching the DLS design.
 *
 * The Map Styles trigger and panel are the same `BasemapGalleryTool` legacy
 * theme uses for this route - a self-contained, fixed-top-positioned panel
 * portaled to `document.body` - rather than a custom panel anchored to the
 * map's own live bottom-right corner.
 *
 * ```typescript
 *
 *  import {GeofenceWidgetCluster} from '@ion/features/src/maps/tools/GeofenceWidgetCluster';
 *
 *  <GeofenceWidgetCluster map={map}
 *    view={view}
 *    createOSMBasemap={createOSMBasemap}
 *    onBasemapChanged={onBasemapChanged}
 *    activeTool={activeTool}
 *    setActiveTool={setActiveTool} />
 *
 * ```
 */
export const GeofenceWidgetCluster = ({
  map,
  view,
  createOSMBasemap,
  onBasemapChanged,
  activeTool,
  setActiveTool,
  isDisableInitialExtent = false,
  callBack,
  showHome = true,
  showBasemapGallery = true,
  hideFullScreenOnPhone = false
}: GeofenceWidgetClusterProps) => {
  const classes          = useStyles();
  const isPhone          = useIsPhone();
  const basemapButtonRef = useRef<HTMLDivElement>(null);
  const buttonOffset     = useMapBottomRightOffset(view, basemapButtonRef);
  const rightOffset      = buttonOffset?.right;
  const topOffset        = buttonOffset
    ? Math.max(MIN_TOP_OFFSET, buttonOffset.top - ESTIMATED_PANEL_HEIGHT - BUTTON_TO_PANEL_GAP)
    : undefined;

  const onZoomOut = function() {
    const {zoom} = view;
    view.goTo({zoom: zoom - 1});
  };

  const onZoomIn = function() {
    const {zoom} = view;
    view.goTo({zoom: zoom + 1});
  };

  return (
    <div className={classes.cluster}>
      {showHome && (
        <HomeToolFactory map={map}
          view={view}
          isDisableInitialExtent={isDisableInitialExtent}
          callBack={callBack} />
      )}
      {!(hideFullScreenOnPhone && isPhone) && (
        <FullScreenToolFactory view={view} />
      )}
      <div className={classes.bottomRow}>
        <ZoomInOutTool isVertical={false}
          style={noZoomToolSpacing}
          onZoomOut={onZoomOut}
          onZoomIn={onZoomIn} />
        {showBasemapGallery && (
          <div ref={basemapButtonRef}>
            <BasemapGalleryTool map={map}
              view={view}
              usePortal
              topOffset={topOffset}
              rightOffset={rightOffset}
              createOSMBasemap={createOSMBasemap}
              onBasemapChanged={onBasemapChanged}
              activeTool={activeTool}
              setActiveTool={setActiveTool}
              isAdminGeofence />
          </div>
        )}
      </div>
    </div>
  );
};

export default GeofenceWidgetCluster;
